/**
 * useArticlePresence — lightweight "who's editing this article" presence.
 *
 * PRESENCE ONLY. We reuse the existing YJS websocket (CustomWebsocketProvider)
 * purely as an awareness channel: an empty throwaway Y.Doc that NEVER sends
 * content updates. Article content, saves, and locking are completely untouched.
 *
 * Shared-account handling: three internal users share one Cognito login, so the
 * server stamps every awareness message with the same `from` (= user.sub) and the
 * provider's awareness Map collapses them into a single entry. We therefore ignore
 * the provider's map keying and instead re-key on the per-session `sessionId`
 * carried inside each payload, reconstructing the live set from a heartbeat + TTL.
 *
 * Inert (returns no peers, opens no socket) when the YJS feature flag / endpoint
 * are absent, so the editor is byte-for-byte unchanged when disabled.
 */
import { Auth } from "aws-amplify";
import { useCallback, useEffect, useRef, useState } from "react";
import { CustomWebsocketProvider } from "src/providers/CustomWebsocketProvider";
import {
  colorForSession,
  getDisplayName,
  getSessionId,
  setStoredDisplayName,
} from "src/utils/presenceIdentity";
import * as Y from "yjs";

export interface PresencePeer {
  sessionId: string;
  displayName: string;
  color: string;
  lastActive: number;
  /** "human" for a person's editor session, "agent" for a bot like Hermes. */
  kind: "human" | "agent";
  /** Monotonic election claim; within a tier the claim breaks ties (see election). */
  claim: number;
  /**
   * How the hold was acquired:
   *  - "auto": implicit hold because the tab found nobody else editing. EARLIER auto
   *    holders outrank later ones, so an incumbent always beats a freshly-opened tab.
   *  - "explicit": a deliberate Take Over. Outranks every auto hold, and among explicit
   *    holds the MOST RECENT wins (that's the whole point of taking over).
   * An agent (kind "agent") is treated as explicit even if this field is absent.
   */
  holdType: "auto" | "explicit";
}

interface UseArticlePresenceOptions {
  enabled?: boolean;
}

export interface UseArticlePresenceReturn {
  active: boolean;
  others: PresencePeer[];
  me: { displayName: string; color: string };
  setDisplayName: (name: string) => void;
  /** True if THIS session is the elected single-writer holder. */
  isHolder: boolean;
  /** The elected holder (highest claim), or null when nobody is editing. */
  holder: PresencePeer | null;
  /** Claim the editor lock (bump our claim above everyone's). */
  takeOver: () => void;
  /** Whether this session may edit: presence off, no holder yet, or we hold it. */
  canEdit: boolean;
  /**
   * True once presence has had a chance to learn about existing peers (first
   * awareness sync, or a short timeout after connect). Until then a fresh tab must
   * NOT assume it is alone — gating on this prevents the sync-gap silent take-over.
   */
  settled: boolean;
}

// Heartbeat/TTL are deliberately tight so a departed peer (closed/left tab) is dropped
// quickly — that's when a read-only viewer gets promoted to editor. The shared Cognito
// account means the server's `leave` (keyed by the shared sub) can't identify WHICH
// session left, so we rely on this heartbeat+TTL rather than the leave signal. The 3:1
// heartbeat:TTL ratio leaves a comfortable margin against network jitter (a live peer
// re-announces ~3× before its TTL would expire).
const HEARTBEAT_MS = 4_000; // re-announce so peers (re)learn our presence
const PEER_TTL_MS = 12_000; // drop a peer we haven't heard from in this long
const PRUNE_INTERVAL_MS = 2_000;
const SETTLE_TIMEOUT_MS = 1_500; // max wait for peer awareness before assuming we're alone
// Re-announce ourselves when a peer's awareness arrives, rate-limited to this interval.
// A backgrounded holder's heartbeat TIMER is throttled by the browser, but its inbound
// `onmessage` is NOT — so responding to a foreground peer's heartbeats keeps it alive
// and prevents the read-only viewer from pruning it and flickering into the lock.
const REANNOUNCE_MIN_INTERVAL_MS = 3_000;
// Grace before a viewer auto-promotes after the holder vanishes — absorbs a brief
// reconnect/refocus gap so we don't claim the lock over a holder that's merely quiet.
const PROMOTE_GRACE_MS = 8_000;

const WS_ENDPOINT = process.env.NEXT_PUBLIC_YJS_WS_ENDPOINT || "";
const FLAG_ON = process.env.NEXT_PUBLIC_USE_YJS_SYNC === "true";

export function useArticlePresence(
  articleId: string,
  options: UseArticlePresenceOptions = {}
): UseArticlePresenceReturn {
  const { enabled = true } = options;
  const active = enabled && FLAG_ON && !!WS_ENDPOINT && !!articleId;

  const sessionId = getSessionId();
  const color = colorForSession(sessionId);

  const [displayName, setDisplayNameState] = useState<string>("Guest");
  const [others, setOthers] = useState<PresencePeer[]>([]);
  // Our election claim. 0 = "not editing / no claim". Take-over bumps it above
  // every peer's claim so the max-claim election makes us the holder.
  const [myClaim, setMyClaim] = useState<number>(0);
  // Whether OUR current hold is implicit (found nobody) or an explicit Take Over.
  // Drives the tiered election so a new tab's auto-hold can never beat an incumbent.
  const [myHoldType, setMyHoldType] = useState<"auto" | "explicit">("auto");
  // False until we've had a chance to learn about existing peers (first awareness
  // sync or a short post-connect timeout). A fresh tab stays read-only until this is
  // true, so the sync gap can't be misread as "I'm alone" → silent take-over.
  const [settled, setSettled] = useState<boolean>(false);

  const providerRef = useRef<CustomWebsocketProvider | null>(null);
  const peersRef = useRef<Map<string, PresencePeer>>(new Map());
  const displayNameRef = useRef(displayName);
  displayNameRef.current = displayName;
  const myClaimRef = useRef(myClaim);
  myClaimRef.current = myClaim;
  const myHoldTypeRef = useRef(myHoldType);
  myHoldTypeRef.current = myHoldType;
  const othersRef = useRef<PresencePeer[]>(others);
  othersRef.current = others;
  // When we last broadcast our awareness — drives the rate-limited re-announce.
  const lastPublishAtRef = useRef(0);
  // Whether we've ever observed another session holding the lock this connection.
  // Lone users (never saw a holder) auto-hold instantly; otherwise we wait a grace.
  const everSawHolderRef = useRef(false);

  // Resolve the initial display name on the client (avoids SSR hydration
  // mismatch): chosen name from localStorage, else a per-session alias.
  useEffect(() => {
    setDisplayNameState(getDisplayName());
  }, []);

  const publish = useCallback(() => {
    const provider = providerRef.current;
    if (!provider) return;
    provider.setLocalAwareness({
      sessionId,
      displayName: displayNameRef.current,
      color,
      lastActive: Date.now(),
      kind: "human",
      claim: myClaimRef.current,
      holdType: myHoldTypeRef.current,
    });
    lastPublishAtRef.current = Date.now();
  }, [sessionId, color]);

  // Re-announce in response to a peer's awareness, but no more than once per
  // REANNOUNCE_MIN_INTERVAL_MS so two live tabs don't ping-pong each other forever.
  // This runs from the (non-throttled) onmessage path, so a backgrounded holder keeps
  // re-announcing as long as a foreground peer keeps heartbeating at it.
  const reannounce = useCallback(() => {
    if (Date.now() - lastPublishAtRef.current >= REANNOUNCE_MIN_INTERVAL_MS) publish();
  }, [publish]);

  useEffect(() => {
    if (!active) {
      setOthers([]);
      return;
    }

    const getToken = async (): Promise<string> => {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    };

    const ydoc = new Y.Doc(); // empty + never mutated → no content is ever sent
    const provider = new CustomWebsocketProvider({
      wsEndpoint: WS_ENDPOINT,
      roomId: `article:${articleId}`,
      ydoc,
      getToken,
    });
    providerRef.current = provider;
    const peers = peersRef.current;

    // Fresh connection (mount / article change / reconnect): start with a clean slate so
    // re-announce isn't suppressed and the promotion grace doesn't carry over.
    lastPublishAtRef.current = 0;
    everSawHolderRef.current = false;

    // Settle: we're "settled" once the first awareness frame arrives (we now know
    // who else is here) or SETTLE_TIMEOUT_MS passes (nobody else responded → we're
    // genuinely alone). Until then canEdit stays false so we can't steal the lock.
    setSettled(false);
    const settleTimer = setTimeout(() => setSettled(true), SETTLE_TIMEOUT_MS);

    // Only re-render when the MEANINGFUL peer set changes (membership / name /
    // color / kind / claim) — NOT on every heartbeat's lastActive bump. Frequent
    // re-renders of the editor tree are wasteful and can desync portal-backed DOM.
    let lastSig = "";
    const commitOthers = () => {
      const arr = Array.from(peers.values());
      const sig = arr
        .map((p) => `${p.sessionId}|${p.displayName}|${p.color}|${p.kind}|${p.claim}`)
        .sort()
        .join(";");
      if (sig !== lastSig) {
        lastSig = sig;
        setOthers(arr);
      }
    };

    // The provider hands us its awareness Map (keyed by the shared `from`, so it
    // may hold only the most-recent sender). We iterate the values and re-key by
    // the per-session id inside each payload.
    const onAwareness = (states: Map<string, any>) => {
      const now = Date.now();
      let sawNew = false;
      let sawPeer = false;
      states.forEach((data) => {
        if (!data || typeof data !== "object" || !data.sessionId) return;
        if (data.sessionId === sessionId) return; // ignore our own echo
        sawPeer = true;
        if (!peers.has(data.sessionId)) sawNew = true;
        peers.set(data.sessionId, {
          sessionId: data.sessionId,
          displayName:
            typeof data.displayName === "string" && data.displayName.trim()
              ? data.displayName
              : "Guest",
          color: typeof data.color === "string" ? data.color : "#9CA3AF",
          lastActive: now,
          kind: data.kind === "agent" ? "agent" : "human",
          claim: typeof data.claim === "number" ? data.claim : 0,
          holdType: data.holdType === "explicit" ? "explicit" : "auto",
        });
      });
      commitOthers();
      // Settle as soon as we KNOW of a peer (fast path — we can trust the election).
      // An empty awareness frame does NOT settle us: the other tab's awareness may
      // simply not have propagated yet, and settling on it would reopen the steal
      // window. If we're genuinely alone, the SETTLE_TIMEOUT_MS fallback settles us.
      if (peers.size > 0) setSettled(true);
      // A peer we hadn't seen just appeared — re-announce immediately so they learn
      // about us promptly. Otherwise, if we heard from ANY peer, re-announce
      // rate-limited — this is what keeps a backgrounded holder alive (it replies to
      // the foreground viewer's heartbeats via the non-throttled onmessage path).
      if (sawNew) publish();
      else if (sawPeer) reannounce();
    };

    const onStatus = (evt: { status: string }) => {
      if (evt.status === "connected") publish();
    };

    provider.on("awareness", onAwareness);
    provider.on("status", onStatus);

    // Prime local awareness so it is sent as soon as the socket opens.
    publish();

    const heartbeat = setInterval(publish, HEARTBEAT_MS);
    const prune = setInterval(() => {
      const cutoff = Date.now() - PEER_TTL_MS;
      let changed = false;
      peers.forEach((peer, key) => {
        if (peer.lastActive < cutoff) {
          peers.delete(key);
          changed = true;
        }
      });
      if (changed) commitOthers();
    }, PRUNE_INTERVAL_MS);

    return () => {
      clearInterval(heartbeat);
      clearInterval(prune);
      clearTimeout(settleTimer);
      provider.off("awareness", onAwareness);
      provider.off("status", onStatus);
      provider.destroy();
      providerRef.current = null;
      peers.clear();
      ydoc.destroy();
      setOthers([]);
      setSettled(false);
    };
  }, [active, articleId, sessionId, publish, reannounce]);

  const setDisplayName = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      const next = trimmed || "Guest";
      setStoredDisplayName(trimmed);
      setDisplayNameState(next);
      displayNameRef.current = next;
      publish(); // broadcast the rename immediately
    },
    [publish]
  );

  // Re-broadcast as soon as our claim changes so every peer re-elects instantly.
  useEffect(() => {
    if (active) publish();
  }, [myClaim, active, publish]);

  // Claim the editor lock. `type` defaults to "explicit" (a deliberate Take Over from
  // the button/modal); the internal auto-hold passes "auto". The claim is bumped above
  // every peer's so an EXPLICIT take-over always wins; for an auto-hold the timestamp
  // just records when we started holding (earlier auto-holds win — see the election).
  const takeOver = useCallback((type: "auto" | "explicit" = "explicit") => {
    setMyHoldType(type);
    setMyClaim((prev) => {
      let maxSeen = prev;
      for (const p of othersRef.current) maxSeen = Math.max(maxSeen, p.claim || 0);
      return Math.max(maxSeen + 1, Date.now());
    });
  }, []);

  // Election (pure, same answer on every tab). Tiered so a freshly-opened tab can never
  // out-rank an incumbent: explicit Take Overs (and the agent) beat auto-holds; among
  // explicit holds the MOST RECENT wins; among auto-holds the EARLIEST wins; ties break
  // on smallest sessionId. claim<=0 means "not holding".
  const meParticipant: PresencePeer = {
    sessionId,
    displayName,
    color,
    lastActive: 0, // unused for self; stable so `holder` identity doesn't churn each render
    kind: "human",
    claim: myClaim,
    holdType: myHoldType,
  };
  const tierOf = (p: PresencePeer): number =>
    p.kind === "agent" || p.holdType === "explicit" ? 1 : 0; // explicit/agent = 1 > auto = 0
  // True if `a` should hold the lock over `b` (a strict total order).
  const beats = (a: PresencePeer, b: PresencePeer): boolean => {
    const ta = tierOf(a);
    const tb = tierOf(b);
    if (ta !== tb) return ta > tb; // explicit tier outranks auto tier
    if (a.claim !== b.claim) {
      // explicit: most-recent (higher) wins; auto: earliest (lower) wins.
      return ta === 1 ? a.claim > b.claim : a.claim < b.claim;
    }
    return a.sessionId < b.sessionId; // deterministic tie-break
  };
  let holder: PresencePeer | null = null;
  for (const p of [...others, meParticipant]) {
    if ((p.claim || 0) <= 0) continue;
    if (!holder || beats(p, holder)) holder = p;
  }
  const isHolder = !!holder && holder.sessionId === sessionId;
  // Remember that we've seen another session hold the lock — gates the promotion grace
  // AND the editability fast-path below.
  if (holder && !isHolder) everSawHolderRef.current = true;
  // Editable when presence is off (today's behavior), or — once SETTLED — when we hold
  // the lock. The "nobody holds yet" fast-path is allowed ONLY for a genuine lone user
  // (never saw a holder); a viewer whose holder merely went quiet must STAY read-only
  // through the promotion grace until `takeOver("auto")` actually fires `isHolder` —
  // otherwise the editor would flip editable during the grace (the flicker we're fixing).
  const canAutoEditWhenUnheld =
    holder === null && myClaim === 0 && !everSawHolderRef.current;
  const canEdit = !active || (settled && (isHolder || canAutoEditWhenUnheld));

  // Auto-hold when genuinely alone: after settling, if nobody holds the article and we
  // haven't claimed, take it so a lone user just edits (no Take Over click). A tab that
  // settles to find an existing holder stays claim=0 → read-only → must ask explicitly.
  //
  // Grace: if we've previously SEEN a holder this session, wait PROMOTE_GRACE_MS before
  // claiming — a holder that merely went quiet (WS reconnect, or a refocus race) will
  // re-appear within the grace and the effect's cleanup cancels the promotion. A lone
  // user (never saw a holder) promotes instantly. This stops the background-throttling
  // flicker where a viewer briefly stole the lock from a still-present editor.
  useEffect(() => {
    if (!(active && settled && holder === null && myClaim === 0)) return;
    const grace = everSawHolderRef.current ? PROMOTE_GRACE_MS : 0;
    const timer = setTimeout(() => takeOver("auto"), grace);
    return () => clearTimeout(timer);
  }, [active, settled, holder, myClaim, takeOver]);

  return {
    active,
    others,
    me: { displayName, color },
    setDisplayName,
    isHolder,
    holder,
    takeOver,
    canEdit,
    settled,
  };
}
